import missionCodec from '@m1/mission-codec';
import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import { distance } from '@turf/turf';
import { pointerMove } from 'ol/events/condition';
import { Select } from 'ol/interaction';
import { toLonLat } from 'ol/proj';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import actions from '@/actions';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import FenceConverter from '@/helpers/FenceConverter';
import useMissionCommand from '@/hooks/command/mavlink/useMissionCommand';
import { useAddressSelector } from '@/hooks/useMavLinkAddress';
import useRobotTelemetry from '@/hooks/useRobotTelemetry';
import { executeMavLinkCommand } from '@/utils/CommandUtils';
import { createFenceFeatures } from '@/utils/FenceUtil';

export const ArduPilot = Symbol();
export const PX4 = Symbol();

const useMission = (firmware, robot) => {
  const dispatch = useDispatch();
  const address = useAddressSelector(robot.id);
  const homePosition = useRobotTelemetry(robot.id, MESSAGE.HOME_POSITION);
  const commands = useSelector((state) => state.missions[robot.id]?.commands);
  const fences = useSelector((state) => state.missions[robot.id]?.fences);
  const isSynced = useSelector((state) => state.missions[robot.id]?.isSynced);
  const map = OlMap.getMap();
  const missionCommand = useMissionCommand();

  useEffect(() => {
    // 제어권한 미보유 시
    if (!robot.isOwned) return;
    if (!address) return;

    loadMission();
  }, [address?.[0]]);

  useUpdateEffect(() => {
    OlMap.way.remove(robot.id);
    drawCommand();
  }, [homePosition]);

  useUpdateEffect(() => {
    drawCommand();
    drawFence();

    return () => {
      OlMap.way.remove(robot.id);
      OlMap.fence.remove(robot.id);
    };
  }, [robot.color, commands, fences, isSynced]);

  const loadMission = async () => {
    const commands = await downloadCommands();
    const fences = await downloadFences();

    if (commands.length === 0 && fences.length === 0) return;

    dispatch(actions.missions.load(robot.id, { commands, fences }));
    // FC에서 내려받은 미션은 이미 동기화
    dispatch(actions.missions.setSynced(robot.id, true));
  };

  const downloadCommands = async () => {
    const messages = [];

    try {
      const { count } = await executeMavLinkCommand(robot, missionCommand.requestList(0));

      for (let seq = 0; seq < count; seq++) {
        const message = await executeMavLinkCommand(robot, missionCommand.requestItem(seq, 0));
        messages.push(message);
      }
      missionCommand.sendAck(robot, 0, 0);
    } catch (error) {
      missionCommand.sendAck(robot, 1, 0);
      return [];
    }

    switch (firmware) {
      case ArduPilot:
        return missionCodec.mavlink.ardupilot.decode(messages);
      case PX4:
        return missionCodec.mavlink.px4.decode(messages);
      default:
        return;
    }
  };

  const downloadFences = async () => {
    const messages = [];

    try {
      const { count } = await executeMavLinkCommand(robot, missionCommand.requestList(1));

      for (let seq = 0; seq < count; seq++) {
        const message = await executeMavLinkCommand(robot, missionCommand.requestItem(seq, 1));
        messages[seq] = message;
      }
      missionCommand.sendAck(robot, 0, 1);
    } catch (error) {
      missionCommand.sendAck(robot, 1, 1);
      return [];
    }

    return FenceConverter.fromMavLink(messages);
  };

  const drawCommand = () => {
    if (!commands) return;

    // 마커 정의
    const markers = [];
    commands.forEach((command, index) => {
      switch (command.type) {
        case 'doSetRoiLocation':
          markers.push({
            position: command.data.position,
            label: index + 1,
            isRoi: true,
          });
          break;

        case 'navLand':
        case 'navLoiterTime':
        case 'navLoiterToAlt':
        case 'navLoiterTurns':
        case 'navTakeoff':
        case 'navWaypoint':
          markers.push({
            position: command.data.position,
            label: index + 1,
          });
          break;

        case 'navReturnToLaunch':
          if (!homePosition) break;
          markers.push({
            position: homePosition,
            label: index + 1,
          });
          break;

        default:
          break;
      }
    });
    const { paths } = OlMap.way.add(robot.id, markers, robot.color, !isSynced);

    // Mouse over 시 강조
    const handleHover = new Select({
      condition: pointerMove,
      layers: [paths],
    });
    handleHover.on('select', (e) => {
      if (e.selected.length) {
        const { flatCoordinates } = e.selected[0].values_.geometry;
        const distanceValue =
          1000 *
          distance(
            toLonLat([flatCoordinates[0], flatCoordinates[1]]),
            toLonLat([flatCoordinates[2], flatCoordinates[3]]),
          );

        dispatch(actions.callout.setText(`${distanceValue.toFixed(1)}m`));
      } else {
        dispatch(actions.callout.setText(null));
      }
    });
    map.addInteraction(handleHover);
  };

  const drawFence = () => {
    if (!fences) return;

    OlMap.fence.add(robot.id, createFenceFeatures(fences), robot.color);
  };
};

export default useMission;
